// A thin fetch wrapper for the GovChew API. Errors carry the server's message, which is written for
// people; a 401 tells the app to show sign-in.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

type Listener = () => void;
const unauthorized = new Set<Listener>();

/** Called whenever the API says the session is gone, so the app can show sign-in. */
export function onUnauthorized(listener: Listener): () => void {
  unauthorized.add(listener);
  return () => unauthorized.delete(listener);
}

export async function api<T = unknown>(
  path: string,
  { method = "GET", body, signal }: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "same-origin",
    signal,
  });
  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    // an empty or non-JSON body; the status says what happened
  }
  if (response.status === 401 && !path.startsWith("/api/login") && !path.startsWith("/api/invites")) {
    unauthorized.forEach((listener) => listener());
    throw new ApiError(401, "Sign in to continue.");
  }
  if (!response.ok) {
    const detail = (data as { detail?: unknown } | null)?.detail;
    const message =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d.msg ?? "").join("; ")
          : `Request failed (${response.status})`;
    throw new ApiError(response.status, message);
  }
  return data as T;
}

/** A message safe to show: server messages are written for people; anything else is a bug. */
export function errorText(error: unknown, fallback = "Something went wrong. Try again."): string {
  return error instanceof ApiError && error.status < 500 ? error.message : fallback;
}

export const qs = (params: Record<string, string | number | boolean | null | undefined>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? "?" + s : "";
};
